import { useEffect, useState } from "react";
import styled from "styled-components";
import { userContext } from '../../usercontext';
import { fetchWrapper } from "../../utils/apiHandlers";
import Button from "../Button";
import SmallButton from "../SmallButton";
import MessageModal from "../MessageModal";
import theme from "../../theme";
import { MdContentCopy } from "react-icons/md";

interface Props {
  onNavigate?: (id: string) => void;
}

const GetStartedComp: React.FC<Props> = ({ onNavigate }) => {
  const [user, setUser] = useState<any>(null)
  const [secretkey, setsecretkey] = useState("")
  const [modalheader, setmodalheader] = useState('')
  const [modalmessage, setmodalmessage] = useState('')
  const [modalstate, setmodalstate] = useState(false)

  const openModal = (header: string, message: string) => {
    setmodalheader(header)
    setmodalmessage(message)
    setmodalstate(true)
  }

  useEffect(() => {
    const subscription = userContext.user.subscribe((x: any) => setUser(x))
    return () => subscription.unsubscribe()
  }, [])

  const showkey = async () => {
    let response = await fetchWrapper.post("/api/authenticate/showsecretkey", { userid: user.userid })
    if (response.status == 200) {
      setsecretkey(response.secret)
      openModal("Message", "Copy and save your secret key - it will not be shown again.")
    } else {
      openModal("Warning", "Your secret key cannot be revealed. Generate a new one if you lost it.")
    }
  }

  const getnewkey = async () => {
    let response = await fetchWrapper.post("/api/authenticate/gennewsecretkey", { userid: user.userid })
    if (response.status == 200) {
      setsecretkey(response.secret)
      openModal("Message", "Copy and save your secret key - it will not be shown again.")
    }
  }

  const copy = (text: string, label: string) => {
    navigator.clipboard.writeText(text)
    openModal("Copied", `${label} copied to clipboard.`)
  }

  if (!user || user.status !== 200) return null

  const userid = (user.userid || "").toLowerCase()
  const npmLine = "npm i miamigo-chatbot"
  const componentCode = `import AIChat from "miamigo-chatbot"\n\n<AIChat\n  userid="${userid}"\n  supportEmail="support@yourdomain.com"\n  supportPhone="555-555-5555"\n  title="Chatbot"\n  chatbotname="Support"\n  iconColor="#FF6A00"\n  handleQuestion={(q) => tokenizeQuestion(q)}\n/>`
  const serverCode = `// on YOUR server - never expose the secret key in browser code\nconst jwt = require("jsonwebtoken")\nconst secretKey = process.env.MIAMIGO_SECRET_KEY\n\nfunction tokenizeQuestion(question) {\n  return jwt.sign({ question }, secretKey)\n}`

  return (
    <MainContainer>
      <MessageModal header={modalheader} message={modalmessage} state={modalstate} handleClose={() => setmodalstate(false)} progress={false} />
      <Intro>Three steps to put your chatbot on your own website.</Intro>

      <StepGrid>
        <StepCard>
          <StepHead><StepNum>1</StepNum>Install &amp; get your key</StepHead>
          <Code>{npmLine}</Code>
          <KeyRow>
            <KeyBox>{secretkey || "•••••••••••••••••••••"}</KeyBox>
            {secretkey && <MdContentCopy color={theme.colors.primary} style={{ cursor: 'pointer' }} onClick={() => copy(secretkey, "Secret key")} />}
          </KeyRow>
          <ButtonRow>
            <SmallButton text="Show key" handleClick={showkey} />
            <SmallButton text="Get new key" handleClick={getnewkey} backgroundColor={theme.colors.textMuted} />
          </ButtonRow>
          <Hint>Your key authenticates requests from your own server - never expose it in browser code.</Hint>
        </StepCard>

        <StepCard>
          <StepHead><StepNum>2</StepNum>Paste one code block</StepHead>
          <Code style={{ whiteSpace: 'pre-wrap', fontSize: '11px' }}>{componentCode}</Code>
          <SmallButton text="Copy component code" handleClick={() => copy(componentCode, "Component code")} />
          <Code style={{ whiteSpace: 'pre-wrap', fontSize: '11px', marginTop: 8 }}>{serverCode}</Code>
          <SmallButton text="Copy server code" handleClick={() => copy(serverCode, "Server code")} />
          <Hint>The component posts to <code>https://miamigo.ai/api/getanswer</code> with a token your server signs.</Hint>
        </StepCard>

        <StepCard>
          <StepHead><StepNum style={{ background: theme.colors.success }}>3</StepNum>Test it live</StepHead>
          <Hint>No copy-paste guesswork - ask your real bot a question before you go live.</Hint>
          <Button text="Open Test Chatbot →" handleClick={() => onNavigate && onNavigate("test")} />
        </StepCard>
      </StepGrid>

      <Callout>
        <div>
          <CalloutTitle>Also want new customers to find you?</CalloutTitle>
          <CalloutText>Get listed on Ask Amigo, our free local-business directory - separate from this embed, no code needed. Ask us to add your business.</CalloutText>
        </div>
      </Callout>
    </MainContainer>
  );
};

const MainContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 20px;
  width: 100%;
`

const Intro = styled.div`
  font-size: 13px;
  color: ${theme.colors.textMuted};
`

const StepGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: 16px;
  @media (max-width: 900px) {
    grid-template-columns: 1fr;
  }
`

const StepCard = styled.div`
  background: ${theme.colors.surface};
  border: 1px solid ${theme.colors.border};
  border-radius: ${theme.radius.lg};
  box-shadow: ${theme.shadow.card};
  padding: 16px;
  display: flex;
  flex-direction: column;
  gap: 10px;
`

const StepHead = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  font-weight: 800;
  font-size: 14px;
  color: ${theme.colors.text};
`

const StepNum = styled.div`
  width: 22px;
  height: 22px;
  border-radius: 999px;
  background: ${theme.colors.primary};
  color: #fff;
  font-size: 11px;
  font-weight: 800;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
`

const Code = styled.div`
  background: ${theme.colors.primaryDark};
  color: #D6E6F8;
  border-radius: 8px;
  padding: 10px 12px;
  font-size: 12px;
  font-family: ui-monospace, "SF Mono", Menlo, monospace;
  overflow-x: auto;
`

const KeyRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
  background: ${theme.colors.background};
  border: 1px solid ${theme.colors.border};
  border-radius: 8px;
  padding: 8px 10px;
`

const KeyBox = styled.div`
  font-size: 12px;
  color: ${theme.colors.textMuted};
  word-break: break-all;
`

const ButtonRow = styled.div`
  display: flex;
  gap: 8px;
  flex-wrap: wrap;
`

const Hint = styled.div`
  font-size: 11px;
  color: ${theme.colors.textMuted};
  line-height: 1.5;
`

const Callout = styled.div`
  background: ${theme.colors.primaryDark};
  border-radius: ${theme.radius.lg};
  padding: 16px 20px;
  color: #fff;
  display: flex;
  align-items: center;
  gap: 16px;
`

const CalloutTitle = styled.div`
  font-weight: 800;
  font-size: 13px;
`

const CalloutText = styled.div`
  font-size: 12px;
  opacity: .85;
  margin-top: 2px;
`

export default GetStartedComp;
